/* eslint-disable lodash/prefer-matches */
/* eslint-disable import/no-internal-modules */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { useAuth0 } from '#~/auth/index.js';
import { castId, generateId } from '@studio/models';
import type { Device } from '@studio/models/device';
import type { Preset } from '@studio/models/video/preset';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  DeviceSetting as PBDeviceSetting,
  DeviceSetting as PBDeviceSettingValue
} from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type {
  StudioDevice,
  StudioDeviceVideoImagePreset
} from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import { useState } from 'react';
import { handleRestorePreset } from '../settings/helpers.js';
import type { Wiring } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { uploadThumbnail } from '#~/control/preset/index.js';
import { Slicer, useDepot } from '#~/depot/hooks.js';
import type { DeviceSetting } from '#~/device/settings/index.js';
import { comparatorFn } from '#~/models/index.js';
import { pbToJsDeviceSettings } from '#~/protobuf/settings.js';
import { locateRoom } from '#~/room/index.js';
import { filterInterfaceSectionsByType } from '../settings/wiring.js';

const ThumbnailRefreshWaitTime = 5000;

/**
 * Use image settings presets.
 *
 * @param deviceId - Device ID.
 * @returns Image settings presets wiring.
 */
export const useWiring = (deviceId: T_Maybe<Device['id']>): Wiring | undefined => {
  const room = deviceId && locateRoom(deviceId);
  const agent = deviceId && locateProxy(deviceId);

  const { getAccessTokenSilently } = useAuth0();

  const presets =
    useDepot((µ) => {
      if (!room) {
        return undefined;
      }

      return new Slicer(µ).device(room.id, deviceId).video?.presets;
    }) ?? {};

  const [refreshThumbnailTimeout, setRefreshThumbnailTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  if (!deviceId) {
    return undefined;
  }

  const { interfaceSections } = agent?.state.status?.device[deviceId]?.deviceSettings ?? {};

  const compareDeviceSettingsMap = (
    prevMap: Record<string, PBDeviceSettingValue>,
    nextMap: Record<string, PBDeviceSettingValue>,
    opts: { ignoreUndefined?: boolean } = {}
  ): boolean => {
    const compareDeviceSettings = (
      prev: PBDeviceSettingValue,
      next: T_Maybe<PBDeviceSettingValue>
    ): boolean => {
      if (next === undefined) {
        return !!opts.ignoreUndefined;
      }

      return !!(
        prev.direct === next.direct &&
        prev.select === next.select &&
        prev.toggle === next.toggle
      );
    };

    let match = true;

    for (const [key, prev] of Object.entries(prevMap)) {
      const next = nextMap[key];

      match = compareDeviceSettings(prev, next);

      if (!match) {
        break;
      }
    }

    if (!match) {
      return match;
    }

    for (const [key, next] of Object.entries(nextMap)) {
      const prev = prevMap[key];

      match = compareDeviceSettings(next, prev);

      if (!match) {
        break;
      }
    }

    return match;
  };

  const wrapMutableDeviceSetting = (
    deviceSettings: Record<string, PBDeviceSettingValue>
  ): Record<number, PBDeviceSetting> => {
    const deviceSettingsEntries = Object.entries(deviceSettings).map(([key, value]) => [
      Number(key),
      {
        direct: value.direct,
        select: value.select,
        toggle: value.toggle
      }
    ]);

    const deviceSettingsMap: Record<number, PBDeviceSetting> =
      Object.fromEntries(deviceSettingsEntries);

    return deviceSettingsMap;
  };

  const setImagePreset = async (preset: Omit<Preset, 'selected'>): Promise<void> => {
    const token = await getAccessTokenSilently();

    const { deviceSettings, id, name, thumbnail } = preset;

    const rank = Object.keys(presets).length;

    await agent?.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                video: {
                  image: {
                    presets: {
                      [id]: {
                        deviceSettings: wrapMutableDeviceSetting(deviceSettings),
                        name,
                        rank,
                        thumbnail
                      }
                    }
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await agent?.save(token);
  };

  const updateImagePreset = async (id: Preset['id'], preset: Partial<Preset>): Promise<void> => {
    const token = await getAccessTokenSilently();

    const mutablePreset: DeepPartial<StudioDeviceVideoImagePreset> = {};

    if (preset.deviceSettings !== undefined) {
      mutablePreset.deviceSettings = wrapMutableDeviceSetting(preset.deviceSettings);
    }

    if (preset.name) {
      mutablePreset.name = preset.name;
    }

    if (preset.thumbnail) {
      mutablePreset.thumbnail = preset.thumbnail;
    }

    if (preset.rank) {
      mutablePreset.rank = preset.rank;
    }

    await agent?.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                video: {
                  image: {
                    presets: {
                      [id]: mutablePreset
                    }
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await agent?.save(token);
  };

  const getDeviceSettingsSnapshot = (): Record<number, PBDeviceSettingValue> => {
    try {
      const rawSettings = pbToJsDeviceSettings({
        pbControl: agent!.state.control!.device[deviceId]!,
        // @ts-ignore
        pbStatus: agent!.state.status!.device[deviceId]!,
        // Readonly state view; pbToJsDeviceSettings only reads it.
        pbStudio: agent!.state.control!.studio!.device[deviceId]! as StudioDevice
      });

      const settings: Record<number, PBDeviceSettingValue> = {};

      Object.entries(rawSettings ?? {}).forEach(([key, value]) => {
        const setting: DeepPartial<PBDeviceSettingValue> = {
          direct: value.direct !== undefined ? value.direct : undefined,
          select: value.select !== undefined ? value.select : undefined,
          toggle: value.toggle !== undefined ? value.toggle : undefined
        };

        settings[Number(key)] = <PBDeviceSettingValue>(<unknown>setting);
      });

      return settings;
    } catch (e) {
      return {};
    }
  };

  const handleReorderPresets = async (presetIds: Preset['id'][]): Promise<void> => {
    const token = await getAccessTokenSilently();

    const mutation = Object.fromEntries(
      presetIds.map((presetId, index) => [presetId, { rank: index }])
    );

    await agent?.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                video: {
                  image: {
                    presets: mutation
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await agent?.save(token);
  };

  const handleRestorePresetId = async (presetId: Preset['id']): Promise<void> => {
    const settings: Record<number, DeviceSetting> = {};

    const presetDeviceSettings = presets[presetId]?.deviceSettings;

    const imageSettingsKeys = filterInterfaceSectionsByType(
      interfaceSections,
      DeviceRegistryInterfaceSectionType.IMAGE_SETTINGS_TRAY
    );

    Object.entries(presetDeviceSettings ?? {})
      .filter(([key]) => imageSettingsKeys.has(Number(key)))
      .forEach(([key, value]) => {
        settings[Number(key)] = {
          direct: value.direct,
          select: value.select,
          toggle: value.toggle
        };
      });

    await handleRestorePreset(agent, deviceId, settings);

    clearTimeout(refreshThumbnailTimeout);

    // If the preset is still selected after 5 seconds, take a snapshot and updated the thumbnail
    const timeout = setTimeout(() => {
      const currentState = getDeviceSettingsSnapshot();

      if (!presetDeviceSettings) {
        return;
      }

      if (!compareDeviceSettingsMap(currentState, presetDeviceSettings)) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleRefreshPresetThumbnail(presetId).catch(noop);
    }, ThumbnailRefreshWaitTime);

    setRefreshThumbnailTimeout(timeout);
  };

  const handleCreatePreset = async (name?: string): Promise<void> => {
    if (!name) {
      // eslint-disable-next-line no-param-reassign
      name = `Preset #${Object.keys(presets).length + 1}`;
    }

    const token = await getAccessTokenSilently();

    const uuid = generateId<Preset['id']>();
    const deviceSettingsSnap = getDeviceSettingsSnapshot();

    await setImagePreset({
      deviceSettings: deviceSettingsSnap,
      id: uuid,
      name,
      rank: Object.keys(presets).length,
      thumbnail: undefined
    });

    const thumbnail = await uploadThumbnail(agent!.id, deviceId, castId(uuid), token);

    await updateImagePreset(uuid, {
      thumbnail
    });
  };

  const handleDestroyPreset = async (presetId: Preset['id']): Promise<void> => {
    const token = await getAccessTokenSilently();

    await agent?.mutate(
      {},
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                video: {
                  image: {
                    presets: {
                      [presetId]: {}
                    }
                  }
                }
              }
            }
          }
        }
      }
    );

    await agent?.save(token);
  };

  const handleUpdatePresetName = async (id: Preset['id'], name: string): Promise<void> => {
    await updateImagePreset(id, {
      name
    });
  };

  const handleResetPresetSnapshot = async (id: Preset['id']): Promise<void> => {
    const deviceSettings = getDeviceSettingsSnapshot();

    await updateImagePreset(id, {
      deviceSettings
    });
  };

  const handleRefreshPresetThumbnail = async (id: Preset['id']): Promise<void> => {
    const token = await getAccessTokenSilently();
    const thumbnail = await uploadThumbnail(agent!.id, deviceId, castId(id), token);

    await updateImagePreset(id, {
      thumbnail
    });
  };

  const formattedPresets = Object.values(presets).sort(comparatorFn);

  const selectedIds: Preset['id'][] = [];

  for (const preset of formattedPresets) {
    const currentState = getDeviceSettingsSnapshot();

    if (compareDeviceSettingsMap(currentState, preset.deviceSettings)) {
      selectedIds.push(preset.id);
    }
  }

  return {
    handleCreatePreset,
    handleDestroyPreset,
    handleRefreshPresetThumbnail,
    handleReorderPresets,
    handleResetPresetSnapshot,
    handleRestorePresetId,
    handleUpdatePresetName,
    presets: formattedPresets,
    selectedIds
  };
};
